import React, { useCallback, useId } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

/** @typedef {'accept' | 'reject' | 'dismiss'} NotificationDialogAction */

export const NOTIFICATION_ICON_PRESET_KEYS = ['warning', 'error', 'info'];

const PRESET_ICON_CONFIG = {
  warning: { Icon: WarningIcon, color: 'warning.main' },
  error: { Icon: ErrorIcon, color: 'error.main' },
  info: { Icon: InfoIcon, color: 'info.main' },
};

const DEFAULT_LABELS = {
  accept: 'Accept',
  reject: 'Reject',
  dismiss: 'Dismiss',
};

/**
 * Reusable notification / confirmation dialog (MUI).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} [props.onClose] — Fired on backdrop/Escape and after an action when closeOnAction is true.
 * @param {string} [props.title]
 * @param {string} [props.message] — Plain text body; ignored if children are passed.
 * @param {React.ReactNode} [props.children] — Rich body content; takes precedence over message.
 * @param {'warning'|'error'|'info'} [props.iconPreset='info'] — Preset icon and accent color.
 * @param {React.ReactNode} [props.icon] — Overrides preset icon (keeps preset color unless you pass a styled node).
 * @param {NotificationDialogAction[]} [props.buttons=['dismiss']] — Which actions to show, left-to-right: reject, dismiss, accept.
 * @param {Partial<Record<NotificationDialogAction, string>>} [props.buttonLabels] — Override default button text.
 * @param {Partial<Record<NotificationDialogAction, (event?: React.SyntheticEvent) => void | Promise<void>>>} [props.onAction] — Handlers per action key.
 * @param {(action: NotificationDialogAction, event?: React.SyntheticEvent) => void | Promise<void>} [props.onAnyAction] — Called after the specific onAction handler for that key (if any).
 * @param {boolean} [props.closeOnAction=true] — Call onClose after a button click (after handlers).
 * @param {boolean} [props.disableBackdropClick=false]
 * @param {string} [props['aria-labelledby']]
 * @param {string} [props['aria-describedby']]
 */
export default function NotificationDialog({
  open,
  onClose,
  title = 'Notice',
  message,
  children,
  iconPreset = 'info',
  icon: iconOverride,
  buttons = ['dismiss'],
  buttonLabels = {},
  onAction = {},
  onAnyAction,
  closeOnAction = true,
  disableBackdropClick = false,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) {
  const defaultTitleId = useId();
  const defaultDescId = useId();
  const titleId = ariaLabelledBy ?? defaultTitleId;
  const descriptionId = ariaDescribedBy ?? defaultDescId;

  const labels = { ...DEFAULT_LABELS, ...buttonLabels };

  const handleClose = useCallback(
    (_event, reason) => {
      if (reason === 'backdropClick' && disableBackdropClick) return;
      if (onClose) onClose();
    },
    [onClose, disableBackdropClick]
  );

  const runAction = useCallback(
    async (action, event) => {
      const specific = onAction[action];
      if (typeof specific === 'function') {
        await specific(event);
      }
      if (typeof onAnyAction === 'function') {
        await onAnyAction(action, event);
      }
      if (closeOnAction && onClose) {
        onClose();
      }
    },
    [onAction, onAnyAction, closeOnAction, onClose]
  );

  const preset = PRESET_ICON_CONFIG[iconPreset] || PRESET_ICON_CONFIG.info;
  const PresetIcon = preset.Icon;
  const titleIcon =
    iconOverride !== undefined && iconOverride !== null ? (
      iconOverride
    ) : (
      <PresetIcon sx={{ color: preset.color, fontSize: 28 }} aria-hidden />
    );

  /** @type {NotificationDialogAction[]} */
  const order = ['reject', 'dismiss', 'accept'];
  const visible = order.filter((key) => buttons.includes(key));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pr: 6,
        }}
      >
        {titleIcon}
        <Typography component="span" variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent id={descriptionId}>
        {children != null ? (
          children
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {message ?? ''}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        {visible.map((action) => {
          const isAccept = action === 'accept';
          const isReject = action === 'reject';
          return (
            <Button
              key={action}
              variant={isAccept ? 'contained' : isReject ? 'outlined' : 'text'}
              color={isAccept ? 'primary' : isReject ? 'error' : 'inherit'}
              onClick={(e) => runAction(action, e)}
            >
              {labels[action]}
            </Button>
          );
        })}
      </DialogActions>
    </Dialog>
  );
}
